import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiProcessStepBlobKind, LoadStatus } from 'constants/aiProcessRuns';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { loadStepBlobAction, selectStepBlob, selectStepBlobStatus } from 'store/aiProcessRuns';

import { RAW_TABS } from '../ExecutionDetailDrawer.constants';
import {
  messageMetaStyles,
  rawBlobStyles,
  rawTabsStyles,
  rawTabStyles,
} from '../ExecutionDetailDrawer.styles';

import { formatBlob } from './StepRawArtifacts.utils';

const EMPTY_STATE_COPY = {
  [AiProcessStepBlobKind.INPUT]: 'No input was recorded for this step.',
  [AiProcessStepBlobKind.OUTPUT]: 'No structured output — the step ended before producing any.',
  [AiProcessStepBlobKind.ERROR]: 'No error detail was stored for this step.',
};

const LOADING_COPY = 'Loading…';
const PENDING_LOAD_STATUSES = new Set([LoadStatus.IDLE, LoadStatus.LOADING]);

const StepRawArtifacts = ({ stepExecutionId, isFailed }) => {
  const dispatch = useDispatch();
  // A failed step opens on the Error artifact (where the captured exception
  // lives); everything else opens on Input as before.
  const [activeKind, setActiveKind] = useState(
    isFailed ? AiProcessStepBlobKind.ERROR : AiProcessStepBlobKind.INPUT,
  );

  const inputBlob = useSelector(selectStepBlob(stepExecutionId, AiProcessStepBlobKind.INPUT));
  const outputBlob = useSelector(selectStepBlob(stepExecutionId, AiProcessStepBlobKind.OUTPUT));
  const errorBlob = useSelector(selectStepBlob(stepExecutionId, AiProcessStepBlobKind.ERROR));
  const activeStatus = useSelector(selectStepBlobStatus(stepExecutionId, activeKind));

  const blobsByKind = useMemo(
    () => ({
      [AiProcessStepBlobKind.INPUT]: inputBlob,
      [AiProcessStepBlobKind.OUTPUT]: outputBlob,
      [AiProcessStepBlobKind.ERROR]: errorBlob,
    }),
    [inputBlob, outputBlob, errorBlob],
  );

  useEffectSafeStrictMode(() => {
    dispatch(loadStepBlobAction({ stepExecutionId, kind: activeKind }));
  }, [dispatch, stepExecutionId, activeKind]);

  // Eagerly pull the error blob for a failed step so the Error tab is reachable
  // even when the user lands on a different artifact first — previously nothing
  // ever fetched it, so the tab (and the real error) never appeared.
  useEffectSafeStrictMode(() => {
    if (isFailed) {
      dispatch(loadStepBlobAction({ stepExecutionId, kind: AiProcessStepBlobKind.ERROR }));
    }
  }, [dispatch, stepExecutionId, isFailed]);

  const onTabClick = useCallback(
    value => () => {
      setActiveKind(value);
    },
    [],
  );

  const activeBlob = blobsByKind[activeKind];
  const formatted = formatBlob(activeBlob);
  const emptyCopy = PENDING_LOAD_STATUSES.has(activeStatus)
    ? LOADING_COPY
    : (EMPTY_STATE_COPY[activeKind] ?? 'No data for this step.');

  const visibleTabs = RAW_TABS.filter(tab => {
    if (tab.value !== AiProcessStepBlobKind.ERROR) {
      return true;
    }

    return isFailed || !_.isNil(errorBlob);
  });

  return (
    <Box>
      <Box sx={rawTabsStyles}>
        {visibleTabs.map(tab => (
          <Box
            key={tab.value}
            sx={rawTabStyles(tab.value === activeKind)}
            onClick={onTabClick(tab.value)}
          >
            <Typography variant="bold12">{tab.label}</Typography>
          </Box>
        ))}
      </Box>
      {_.isNil(formatted) ? (
        <Typography sx={messageMetaStyles} variant="regular12">
          {emptyCopy}
        </Typography>
      ) : (
        <Box sx={rawBlobStyles}>{formatted}</Box>
      )}
    </Box>
  );
};

StepRawArtifacts.propTypes = {
  stepExecutionId: PropTypes.string.isRequired,
  isFailed: PropTypes.bool,
};

export default memo(StepRawArtifacts);
